import { useState } from 'react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  cn,
  ColorInput,
  FormField,
  FormFieldError,
  FormFieldHint,
  FormFieldLabel,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { SLUG_HELPER_TEXT } from './company-form.schema';
import { UploadIcon } from './icons';

export interface CompanyCreateValues {
  name: string;
  cnpj: string;
  slug: string;
  website: string;
  primaryColor: string;
}

export interface CompanyCreatePageProps {
  values: CompanyCreateValues;
  errors: Record<string, string>;
  logoFileName: string | null;
  /** Object URL of the picked logo (null until a valid file lands). Renders
   *  as a small avatar next to the page title for instant feedback. */
  logoPreviewUrl: string | null;
  isSubmitting: boolean;
  /** Shown above the form footer when a top-level (non-field) submit error occurred. */
  submitError?: string | null;
  onFieldChange: (field: keyof CompanyCreateValues, value: string) => void;
  onLogoChange: (file: File | null) => void;
  onSubmit: () => void;
}

export function CompanyCreatePage({
  values,
  errors,
  logoFileName,
  logoPreviewUrl,
  isSubmitting,
  submitError,
  onFieldChange,
  onLogoChange,
  onSubmit,
}: CompanyCreatePageProps) {
  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="home" />

      <main className="flex flex-1 flex-col items-center gap-8 bg-card px-8 pt-8 pb-16">
        <div className="flex w-full max-w-[630px] flex-col gap-8">
          <div className="flex flex-row items-center gap-3">
            {logoPreviewUrl ? (
              <Avatar size="md" shape="square" data-testid="company-form-logo-preview">
                <AvatarImage src={logoPreviewUrl} alt={values.name || 'Logo da empresa'} />
                <AvatarFallback>{(values.name || 'E').charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
            ) : null}
            <h1 className="text-4xl font-medium leading-none tracking-tight text-foreground">
              Criação de empresa
            </h1>
          </div>

          <form
            className="flex flex-col gap-5"
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
            noValidate
          >
            <FormField error={Boolean(errors.name)}>
              <FormFieldLabel>Nome da empresa</FormFieldLabel>
              <Input
                value={values.name}
                placeholder="Ex.: Empresa LTDA."
                variant={errors.name ? 'destructive' : 'default'}
                onChange={(event) => onFieldChange('name', event.target.value)}
                data-testid="company-form-name"
              />
              <FormFieldError>{errors.name}</FormFieldError>
            </FormField>

            <FormField error={Boolean(errors.cnpj)}>
              <FormFieldLabel>CNPJ</FormFieldLabel>
              <Input
                value={values.cnpj}
                placeholder="Ex.: 00.000.000/0000-00"
                inputMode="numeric"
                autoComplete="off"
                maxLength={18}
                variant={errors.cnpj ? 'destructive' : 'default'}
                onChange={(event) => onFieldChange('cnpj', event.target.value)}
                data-testid="company-form-cnpj"
              />
              <FormFieldError>{errors.cnpj}</FormFieldError>
            </FormField>

            <FormField error={Boolean(errors.slug)}>
              <SlugLabel />
              <Input
                value={values.slug}
                placeholder="Ex.: casas-bahia"
                variant={errors.slug ? 'destructive' : 'default'}
                onChange={(event) => onFieldChange('slug', event.target.value.toLowerCase())}
                data-testid="company-form-slug"
              />
              <FormFieldHint>{SLUG_HELPER_TEXT}</FormFieldHint>
              <FormFieldError>{errors.slug}</FormFieldError>
            </FormField>

            <FormField error={Boolean(errors.website)}>
              <FormFieldLabel>Site</FormFieldLabel>
              <Input
                value={values.website}
                placeholder="Ex.: https://www.empresa.com.br"
                inputMode="url"
                variant={errors.website ? 'destructive' : 'default'}
                onChange={(event) => onFieldChange('website', event.target.value)}
                data-testid="company-form-website"
              />
              <FormFieldError>{errors.website}</FormFieldError>
            </FormField>

            <FormField error={Boolean(errors.primaryColor)}>
              <FormFieldLabel>Cor primária</FormFieldLabel>
              <div className="flex flex-row items-center gap-2">
                <Input
                  value={values.primaryColor}
                  placeholder="Ex.: #1D8674"
                  variant={errors.primaryColor ? 'destructive' : 'default'}
                  onChange={(event) => onFieldChange('primaryColor', event.target.value)}
                  data-testid="company-form-primary-color"
                />
                <ColorInput
                  value={values.primaryColor}
                  invalid={Boolean(errors.primaryColor)}
                  onChange={(hex) => onFieldChange('primaryColor', hex)}
                  data-testid="company-form-primary-color-picker"
                />
              </div>
              <FormFieldError>{errors.primaryColor}</FormFieldError>
            </FormField>

            <FormField error={Boolean(errors.logo)}>
              <FormFieldLabel>Logo da empresa</FormFieldLabel>
              <LogoFileInput
                fileName={logoFileName}
                hasError={Boolean(errors.logo)}
                onChange={onLogoChange}
              />
              <FormFieldHint>
                Envie em .JPG ou .PNG, com no máximo 5 MB e dimensão de 192 × 192 px. O fundo deve
                ter a cor primária, com a logo em aplicação positiva ou negativa.
              </FormFieldHint>
              <FormFieldError>{errors.logo}</FormFieldError>
            </FormField>

            {submitError ? (
              <p
                className="text-sm leading-5 text-destructive"
                role="alert"
                data-testid="company-form-submit-error"
              >
                {submitError}
              </p>
            ) : null}

            <div className="flex flex-row pt-3">
              <Button type="submit" loading={isSubmitting} data-testid="company-form-submit">
                Criar empresa
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

/**
 * Slug field label with a focusable info icon → Tooltip explaining the regex
 * convention. Radix Tooltip ships hover- and focus-driven by default, so a
 * mouse user clicking the icon on a desktop without hovering first gets
 * nothing — the controlled `open` state below makes click also toggle
 * disclosure. Hover and keyboard focus still drive `open` via Radix.
 */
function SlugLabel() {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-row items-center gap-1.5">
      <FormFieldLabel>Slug</FormFieldLabel>
      <TooltipProvider delayDuration={0}>
        <Tooltip open={open} onOpenChange={setOpen}>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Padrão do slug"
              className="text-muted-foreground hover:text-foreground"
              data-testid="company-form-slug-info"
              onClick={() => setOpen((value) => !value)}
            >
              <InfoIcon className="size-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent>{SLUG_HELPER_TEXT}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

interface LogoFileInputProps {
  fileName: string | null;
  hasError: boolean;
  onChange: (file: File | null) => void;
}

function LogoFileInput({ fileName, hasError, onChange }: LogoFileInputProps) {
  return (
    <label
      className={cn(
        'flex h-9 w-full cursor-pointer flex-row items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm shadow-2xs transition-colors',
        'focus-within:ring-2 focus-within:ring-primary/40 hover:bg-muted/30',
        hasError ? 'border-destructive' : 'border-input'
      )}
    >
      <UploadIcon className="size-4 text-foreground" />
      <span
        className={cn(
          'flex-1 truncate font-medium',
          fileName ? 'text-foreground' : 'text-foreground'
        )}
      >
        {fileName ?? 'Clique para selecionar um arquivo'}
      </span>
      <input
        type="file"
        accept="image/jpeg,image/png"
        className="sr-only"
        data-testid="company-form-logo"
        onChange={(event) => {
          const file = event.target.files?.[0] ?? null;
          onChange(file);
        }}
      />
    </label>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  );
}
